import axios from 'axios';

// 案2-d 同一オリジン化以降のデフォルトは相対パス '/api'。
// 取引先入口SWA / 管理画面 / ローカル開発のいずれもブラウザ側のオリジンに合わせて API を呼ぶ。
// ローカル開発で Function App を別ポートで上げる場合のみ REACT_APP_API_URL=http://localhost:7071/api を渡す。
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? '/api',
  withCredentials: true, // 管理画面はCookieセッション、取引先APIは公開なので副作用なし
});

// v0.15 (2026-09-29): 取引先画面は OTP 検証で受け取った本人確認トークンを Authorization で送る。
// 管理画面（console bundle）には invoice_auth が無いので何も付かない。
api.interceptors.request.use(cfg => {
  try {
    const raw = sessionStorage.getItem('invoice_auth');
    const token = raw ? JSON.parse(raw)?.token : null;
    if (token) cfg.headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // sessionStorage が壊れていても通常のリクエストは止めない
  }
  return cfg;
});

export default api;

// OTP
export const sendOtp = (email: string, receivingCompanyCode: string) =>
  api.post('/otp/send', { email, receivingCompanyCode });

// v0.13: メアド重複許容に伴い、OTP検証後は登録完了業者を配列で返す（1件→自動/複数→選択）。
export interface LoginVendorOption {
  companyCode: string;
  companyName: string;
  receivingCompanyCode: string;
  receivingCompanyName: string;
}
export const verifyOtp = (email: string, code: string) =>
  api.post<{ verified: boolean; vendors: LoginVendorOption[]; token: string; expiresIn: number }>('/otp/verify', { email, code });

// 取引先
// v0.13 要件1/4: 招待は MF取込済み業者(CompanyCode)に対してメアドを付与して送る。
//   空行の飛び込み招待は廃止。税区分未設定の業者は API 側で 400（ゲート）。
export const inviteVendorByCode = (companyCode: string, email: string) =>
  api.post(`/vendors/${companyCode}/invite`, { email });

export const registerVendor = (data: RegisterVendorPayload) =>
  api.post('/vendors/register', data);

// v0.13 要件4: 登録画面の事前充填（トークン識別・OTP非消費）
export interface VendorPrefill {
  companyName: string;
  invoiceNumber?: string | null;
  taxCategoryId?: number | null;
  receivingCompanyCode?: string | null;
  email?: string | null;
}
export const getVendorPrefill = (token: string) =>
  api.get<VendorPrefill>('/vendors/prefill', { params: { token } });

export const getVendors = (receivingCompanyCode?: string) =>
  api.get('/vendors', { params: { receivingCompanyCode } });

export const cancelInviteVendor = (companyCode: string) =>
  api.delete(`/vendors/${companyCode}`);

// v0.7 論理削除（登録完了/MF取込/招待中 すべて対象）
export const softDeleteVendor = (companyCode: string) =>
  api.post(`/vendors/${companyCode}/soft-delete`);

export const restoreVendor = (companyCode: string) =>
  api.post(`/vendors/${companyCode}/restore`);

export const updateVendor = (companyCode: string, data: UpdateVendorPayload) =>
  api.put(`/vendors/${companyCode}`, data);

export const notifyVendorUpdate = (companyCode: string) =>
  api.post(`/vendors/${companyCode}/notify-update`);

export interface UpdateVendorPayload {
  companyName: string;
  contactName: string;
  title?: string;
  invoiceNumber?: string;
  noInvoiceFlag: boolean;
  websiteUrl?: string;
  bankName: string;
  branchName: string;
  accountType: number;
  accountNumber: string;
  accountHolder: string;
}

export const getInvoices = (
  receivingCompanyCode?: string,
  targetMonth?: string,
  receivedMonth?: string,
  settled?: boolean,
) =>
  api.get('/invoices', {
    params: { receivingCompanyCode, targetMonth, receivedMonth, settled: settled ? '1' : undefined },
  });

// v0.14 支払消込（furikomi-checker の reconciled 振込CSVで請求書を合算消込）
export const settlementCandidates = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/settlement/candidates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export interface SettlementConfirmPayload {
  company?: string;
  payDate?: string;
  amount: number;
  batchId?: string;
  transferId?: string;
  receiptIds: number[];
}
export const settlementConfirm = (payload: SettlementConfirmPayload) =>
  api.post('/settlement/confirm', payload);
// v0.16: 消込の取消（合算グループ単位で未消込に戻す）
export const settlementUndo = (groupId: string, receiptIds: number[]) =>
  api.post(`/settlement/${encodeURIComponent(groupId)}/undo`, { receiptIds });

export const registerInvoice = (formData: FormData) =>
  api.post('/invoices', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// v0.5 P1 phase1b: 請求書詳細 / 編集 / 確定切替 / MF債務CSV
export const getInvoiceById = (receiptId: number) =>
  api.get(`/invoices/${receiptId}`);

export interface UpdateInvoicePayload {
  ocrVendorNameRaw?: string | null;
  ocrInvoiceNo?: string | null;
  ocrInvoiceDate?: string | null;
  ocrAmountExcl8?: number | null;
  ocrAmountExcl10?: number | null;
  ocrAmountIncl8?: number | null;
  ocrAmountIncl10?: number | null;
  ocrTotal?: number | null;
  ocrInvoiceRegisterNo?: string | null;
  ocrPaymentMethod?: string | null;

  mfPurchaseCategoryCode?: string | null;
  mfPurchaseCategoryName?: string | null;
  mfTransactionContent?: string | null;
  mfPaymentDueDate?: string | null;
  mfPaymentMethodCode?: string | null;
  mfPaymentMethod?: string | null;
  mfNotes?: string | null;

  vendorCompanyCode?: string | null;
  taxCategoryId?: number | null;
  subject?: string;
  amountIncludingTax?: number;
}
export const updateInvoice = (receiptId: number, data: UpdateInvoicePayload) =>
  api.put(`/invoices/${receiptId}`, data);

export const confirmInvoice = (receiptId: number) =>
  api.post(`/invoices/${receiptId}/confirm`);
export const unconfirmInvoice = (receiptId: number) =>
  api.post(`/invoices/${receiptId}/unconfirm`);

export const exportMfDebtCsv = (subject: string, from?: string, to?: string, onlyConfirmed = true) =>
  api.get('/invoices/export/mf-debt-csv', {
    params: { subject, from, to, onlyConfirmed },
    responseType: 'blob',
  });

// v0.5 P1 phase1b: 取引先 MF/通知列のライト更新 + CSV import
export interface UpdateVendorMfFieldsPayload {
  contactEmail2?: string | null;
  mailSendEnabled?: boolean;
  mfPayeeCode?: string | null;
  mfVendorCode?: string | null;
  mfRegisterNo?: string | null;
  taxCategoryId?: number | null;   // v0.13 要件3: 業者に固定する税区分（0/null で解除）
}
export const updateVendorMfFields = (companyCode: string, data: UpdateVendorMfFieldsPayload) =>
  api.patch(`/vendors/${companyCode}/mf-fields`, data);

export const importVendorsCsv = (file: File, receivingCompanyCode: string, dryRun = false) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('receivingCompanyCode', receivingCompanyCode);
  fd.append('dryRun', dryRun ? 'true' : 'false');
  return api.post('/vendors/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const sendBulkMail = (receivingCompanyCode: string, targetMonth?: string) =>
  api.post('/mail/send-bulk', null, { params: { receivingCompanyCode, targetMonth } });

export const sendIndividualMail = (vendorCompanyCode: string) =>
  api.post('/mail/send-individual', null, { params: { vendorCompanyCode } });

// 受領先会社
export interface ReceivingCompanyPayload {
  companyCode: string;
  companyName: string;
  fromEmailAddress: string;
  spoRootFolderUrl: string;
  adminNotificationEmail: string;
  defaultPaymentMethod?: string | null;
  defaultPaymentCode?: string | null;
  shortCode?: string | null;
  // Function App appsettings から計算される。GET時のみ返却。POST/PUTでは無視される。
  driveFolderUrl?: string | null;
}
export const getReceivingCompanies = () =>
  api.get<ReceivingCompanyPayload[]>('/receiving-companies');
export const createReceivingCompany = (data: ReceivingCompanyPayload) =>
  api.post('/receiving-companies', data);
export const updateReceivingCompany = (code: string, data: ReceivingCompanyPayload) =>
  api.put(`/receiving-companies/${code}`, data);
export const deleteReceivingCompany = (code: string) =>
  api.delete(`/receiving-companies/${code}`);

// お問い合わせ
export const getInquiries = (params?: { status?: number; receivingCompanyCode?: string }) =>
  api.get('/inquiries', { params });
export const createInquiry = (data: { vendorCompanyCode: string; subject: string; body: string }) =>
  api.post('/inquiries', data);
export const updateInquiryStatus = (id: number, status: number) =>
  api.patch(`/inquiries/${id}/status`, { status });

// 担当者変更
export const updateVendorContact = (companyCode: string, data: { email: string; contactName: string }) =>
  api.patch(`/vendors/${companyCode}/contact`, data);
export const notifyVendorContactChange = (companyCode: string) =>
  api.post(`/vendors/${companyCode}/notify-contact-change`);

// 社内ユーザ
export const getInternalUsers = () => api.get('/mgmt/users');
export const createInternalUser = (data: { fullName: string; email: string; role: number }) =>
  api.post('/mgmt/users', data);
export const updateInternalUser = (id: number, data: { fullName: string; role: number; isActive: boolean }) =>
  api.patch(`/mgmt/users/${id}`, data);
export const deleteInternalUser = (id: number) => api.delete(`/mgmt/users/${id}`);
export const resendAdminInvite = (id: number) => api.post(`/mgmt/users/${id}/resend-invite`);

// v0.9 MT化 P1: テナントスイッチャー（system_admin限定）
export interface TenantOption {
  code: string;
  name: string;
}
export const getTenants = () => api.get<TenantOption[]>('/mgmt/tenants');
export const setActiveTenant = (code: string | null) =>
  api.post<{ activeTenantCode: string | null; message: string }>('/mgmt/active-tenant', { code });

// 消費税区分
export const getTaxCategories = () => api.get('/tax-categories');
export const createTaxCategory = (data: { categoryName: string; code: string }) =>
  api.post('/tax-categories', data);
export const updateTaxCategory = (id: number, data: { categoryName: string; code: string }) =>
  api.put(`/tax-categories/${id}`, data);
export const deleteTaxCategory = (id: number) => api.delete(`/tax-categories/${id}`);

export interface RegisterVendorPayload {
  token: string;        // v0.13 要件4: 招待トークンで業者を一意特定（email/rc は載せない）
  otpCode: string;
  companyName: string;
  contactName: string;
  title?: string;
  invoiceNumber?: string;
  noInvoiceFlag: boolean;
  websiteUrl?: string;
  bankName: string;
  branchName: string;
  accountType: number;
  accountNumber: string;
  accountHolder: string;
}
